// 845 variant of validator.ts's per-transaction content checks: walks a
// parsed 845 (header, Name loops, PAD-detail loops with nested CTP/QTY, and
// a CTT/AMT summary) into its flat segment list, then runs the shared
// dictionary-driven element checks against it, plus the structural checks
// specific to an 845 (missing BPA, no Name loops, no PAD lines, a missing or
// mismatched CTT count).

import type {EdiElementValue, EdiSegment} from './tokenizer';
import {
  checkElements,
  type PathedSegment,
  type ValidationFinding,
} from './validator';
import type {ItemLoop845, NameLoop845, Parsed845} from './parser845';

function elementValueAsString(
  value: EdiElementValue | undefined
): string | undefined {
  if (value === undefined) return undefined;
  return Array.isArray(value) ? value.join('') : value;
}

function collectNameLoopSegments845(
  loop: NameLoop845,
  path: string
): PathedSegment[] {
  const out: PathedSegment[] = [{segment: loop.n1, path: `${path}.n1`}];
  if (loop.n2) out.push({segment: loop.n2, path: `${path}.n2`});
  if (loop.n3) out.push({segment: loop.n3, path: `${path}.n3`});
  if (loop.n4) out.push({segment: loop.n4, path: `${path}.n4`});
  if (loop.n5) out.push({segment: loop.n5, path: `${path}.n5`});
  if (loop.n6) out.push({segment: loop.n6, path: `${path}.n6`});
  loop.n9.forEach((s, i) => out.push({segment: s, path: `${path}.n9[${i}]`}));
  loop.refs.forEach((s, i) =>
    out.push({segment: s, path: `${path}.refs[${i}]`})
  );
  if (loop.per) out.push({segment: loop.per, path: `${path}.per`});
  return out;
}

function collectItemSegments845(
  item: ItemLoop845,
  path: string
): PathedSegment[] {
  const out: PathedSegment[] = [{segment: item.pad, path: `${path}.pad`}];
  if (item.ctp) out.push({segment: item.ctp, path: `${path}.ctp`});
  item.qty.forEach((s, i) => out.push({segment: s, path: `${path}.qty[${i}]`}));
  return out;
}

export function collectParsedSegments845(
  parsed: Parsed845,
  basePath: string
): PathedSegment[] {
  const out: PathedSegment[] = [];
  const {header} = parsed;
  const push = (segment: EdiSegment | undefined, suffix: string) => {
    if (segment) out.push({segment, path: `${basePath}.${suffix}`});
  };
  const pushAll = (segments: EdiSegment[], suffix: string) => {
    segments.forEach((s, i) =>
      out.push({segment: s, path: `${basePath}.${suffix}[${i}]`})
    );
  };

  push(header.bpa, 'header.bpa');
  push(header.con, 'header.con');
  pushAll(header.refs, 'header.refs');
  push(header.per, 'header.per');
  pushAll(header.dtm, 'header.dtm');
  pushAll(header.msg, 'header.msg');
  pushAll(header.n9, 'header.n9');

  parsed.nameLoops.forEach((loop, i) => {
    out.push(
      ...collectNameLoopSegments845(loop, `${basePath}.nameLoops[${i}]`)
    );
  });
  parsed.items.forEach((item, i) => {
    out.push(...collectItemSegments845(item, `${basePath}.items[${i}]`));
  });

  push(parsed.summary.ctt, 'summary.ctt');
  pushAll(parsed.summary.amt, 'summary.amt');

  return out;
}

export function validateParsed845(
  parsed: Parsed845,
  txPath: string
): ValidationFinding[] {
  const findings: ValidationFinding[] = [];

  if (!parsed.header.bpa) {
    findings.push({
      severity: 'error',
      path: `${txPath}.header.bpa`,
      message:
        'Missing BPA segment — every 845 must open with a Beginning Segment for Price Authorization Acknowledgment/Status.',
    });
  }

  if (parsed.nameLoops.length === 0) {
    findings.push({
      severity: 'error',
      path: `${txPath}.nameLoops`,
      message:
        'No N1 party loops found in this price authorization acknowledgment/status.',
    });
  }

  if (parsed.items.length === 0) {
    findings.push({
      severity: 'error',
      path: `${txPath}.items`,
      message:
        'No PAD price authorization lines found in this price authorization acknowledgment/status.',
    });
  }

  if (!parsed.summary.ctt) {
    findings.push({
      severity: 'error',
      path: `${txPath}.summary.ctt`,
      message:
        'Missing CTT segment — the transaction has no summary line-item count.',
    });
  } else {
    const declared = elementValueAsString(parsed.summary.ctt.elements[0]);
    if (declared !== undefined && Number(declared) !== parsed.items.length) {
      findings.push({
        severity: 'error',
        path: `${txPath}.summary.ctt`,
        message: `CTT01 says ${declared} line item(s) but ${parsed.items.length} PAD line(s) were found.`,
      });
    }
  }

  for (const {segment, path} of collectParsedSegments845(parsed, txPath)) {
    checkElements(segment, path, findings);
  }

  return findings;
}
